import { useState } from 'react';
import { isAdmin, hasPermission, formatMoney } from '@vns/core';
import { motion } from 'framer-motion';
import {
  Repeat,
  Plus,
  Filter,
  RotateCcw,
  Calendar,
  Building2,
  Package,
  Truck,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowRight,
  Boxes,
  Layers
} from 'lucide-react';
import { useSession } from '../lib/session';
import {
  useStockTransfers,
  useDeleteStockTransfer,
  useRestoreStockTransfer,
  type StockTransferRow,
} from '../lib/finance';
import { useLookups } from '../lib/lookups';
import Shell from '../components/Shell';
import StockTransferDrawer from '../components/StockTransferDrawer';
import EditStockTransferDrawer from '../components/EditStockTransferDrawer';
import Confirm, { useConfirm } from '../components/Confirm';
import RowActions from '../components/RowActions';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  StatusChip,
  useToast,
  Select,
} from '../components/ui';

export default function StockTransfers() {
  const actor = useSession((s) => s.actor);
  const [status, setStatus] = useState('approved');
  const [projectId, setProjectId] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StockTransferRow | null>(null);
  const confirm = useConfirm<StockTransferRow>();
  const remove = useDeleteStockTransfer();
  const restore = useRestoreStockTransfer();
  const toast = useToast();
  const { data: lk } = useLookups();
  const { data, isLoading, error, refetch } = useStockTransfers(status, { projectId, materialId });

  async function doDelete() {
    if (!confirm.target) return;
    const row = confirm.target;
    try {
      await remove.mutateAsync(row.id);
      toast.showWithAction(`${row.materialName} transfer removed`, {
        label: 'Undo',
        run: () => restore.mutateAsync(row.id),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the transfer', true);
    } finally {
      confirm.close();
    }
  }

  const filtered = status !== '' || !!projectId || !!materialId;
  const clearFilters = () => {
    setStatus('');
    setProjectId('');
    setMaterialId('');
  };

  const narrowed = status !== 'approved' || !!projectId || !!materialId;

  // KPI Calculations
  const totalCount = data?.length ?? 0;
  const approvedCount = data?.filter((t) => t.approvalStatus === 'approved').length ?? 0;
  const pendingCount = data?.filter((t) => t.approvalStatus === 'pending').length ?? 0;
  const totalValuePaise = data?.reduce((acc, t) => acc + (t.amountPaise ?? 0), 0) ?? 0;

  return (
    <Shell
      title="Stock Transfers"
      crumb="Inventory"
      actions={
        Boolean(actor) && (
          <button
            className="btn btn-primary btn-auto btn-sm"
            onClick={() => setAdding(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
          >
            <Plus size={15} strokeWidth={2.2} />
            <span>Add Transfer</span>
          </button>
        )
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* KPI Metrics */}
        <div className="kpi-row" style={{ marginBottom: 20 }}>
          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Total Transfers</span>
              <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                <Repeat size={14} />
              </div>
            </div>
            <div className="val num" style={{ fontSize: 20 }}>{totalCount}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Dispatched inter-site stock</div>
          </div>

          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Total Transfer Value</span>
              <div style={{ color: '#0284c7', background: 'rgba(2, 132, 199, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                <Boxes size={14} />
              </div>
            </div>
            <div className="val num" style={{ fontSize: 20, color: '#0284c7' }}>{formatMoney(totalValuePaise)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Total material valuation</div>
          </div>

          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Approved & Received</span>
              <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                <CheckCircle2 size={14} />
              </div>
            </div>
            <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>{approvedCount}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Completed site transfers</div>
          </div>

          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Pending Verification</span>
              <div
                style={{
                  color: pendingCount > 0 ? '#ea580c' : 'var(--text-faint)',
                  background: pendingCount > 0 ? 'rgba(234, 88, 12, 0.1)' : 'var(--surface-muted)',
                  padding: 5,
                  borderRadius: 'var(--radius-xs)',
                }}
              >
                <Clock size={14} />
              </div>
            </div>
            <div
              className="val num"
              style={{
                fontSize: 20,
                color: pendingCount > 0 ? '#ea580c' : 'var(--text-soft)',
              }}
            >
              {pendingCount}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Awaiting receiving approval</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div
          className="toolbar stock-transfers-toolbar"
          style={{
            background: 'var(--surface)',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div className="st-filter-item st-filter-status" style={{ minWidth: 180 }}>
            <Select
              value={status}
              onChange={(val) => setStatus(val)}
              placeholder="Status: Approved"
              options={[
                { value: 'approved', label: 'Status: Approved' },
                { value: 'pending', label: 'Status: Pending Approval' },
                { value: 'rejected', label: 'Status: Returned' },
                { value: '', label: 'Status: All Transfers' },
              ]}
            />
          </div>

          <div className="st-filter-item st-filter-project" style={{ minWidth: 200 }}>
            <Select
              value={projectId}
              onChange={(val) => setProjectId(val)}
              placeholder="All Project Sites"
              options={[
                { value: '', label: 'All Project Sites' },
                ...(lk?.projects.map((p) => ({
                  value: p.id,
                  label: p.name,
                })) ?? []),
              ]}
            />
          </div>

          <div className="st-filter-item st-filter-material" style={{ minWidth: 200 }}>
            <Select
              value={materialId}
              onChange={(val) => setMaterialId(val)}
              placeholder="All Materials"
              options={[
                { value: '', label: 'All Materials' },
                ...(lk?.materials.map((m) => ({
                  value: m.id,
                  label: m.name,
                })) ?? []),
              ]}
            />
          </div>

          <div className="grow" />

          {filtered && (
            <button
              className="btn btn-auto btn-sm btn-ghost st-reset-btn"
              onClick={clearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                color: 'var(--text-soft)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <RotateCcw size={13} />
              <span>Reset Filters</span>
            </button>
          )}
        </div>

        {/* Center-Aligned Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={4} cols={9} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="repeat"
                title={status === 'approved' ? 'No stock transfers found' : `Nothing ${status || 'recorded'}`}
                hint={
                  narrowed
                    ? 'No transfer records match your filter criteria. Try resetting the filters.'
                    : hasPermission(actor, 'add_stock_transfer')
                    ? 'Use Add Transfer above, or open a project and record a Site Transfer.'
                    : 'Stock transfer records will appear here.'
                }
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Material</th>
                    <th>Source Site</th>
                    <th>Destination Site</th>
                    <th className="num">Quantity</th>
                    <th className="num">Rate</th>
                    <th className="num">Total Value</th>
                    <th>Vehicle Details</th>
                    <th>Status</th>
                    {isAdmin(actor) && <th style={{ width: 80 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                          <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                          <span>{formatDate(t.date)}</span>
                        </span>
                      </td>

                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {t.materialName}
                        </span>
                      </td>

                      <td>
                        <span style={{ color: 'var(--text-soft)' }}>
                          {t.fromName}
                        </span>
                      </td>

                      <td>
                        <span
                          style={{
                            fontWeight: 600,
                            color: 'var(--brand)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <ArrowRight size={12} />
                          <span>{t.toName}</span>
                        </span>
                      </td>

                      <td className="num">
                        <span
                          style={{
                            fontWeight: 700,
                            color: 'var(--text)',
                            background: 'var(--surface-muted)',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-xs)',
                            border: '1px solid var(--border)',
                          }}
                        >
                          {t.quantity.toLocaleString('en-IN', { maximumFractionDigits: 3 })} {t.unit}
                        </span>
                      </td>

                      <td className="num">
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12.5 }}>
                          {t.ratePaise != null ? `₹${(t.ratePaise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                        </span>
                      </td>

                      <td className="num">
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: t.amountPaise ? 'var(--text)' : 'var(--text-faint)', fontSize: 13 }}>
                          {t.amountPaise != null ? formatMoney(t.amountPaise) : '—'}
                        </span>
                      </td>

                      <td>
                        {t.vehicleNo ? (
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                              color: 'var(--text-soft)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <Truck size={11} style={{ color: 'var(--text-faint)' }} />
                            <span>{t.vehicleNo}</span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-faint)' }}>—</span>
                        )}
                      </td>

                      <td>
                        <StatusChip value={t.approvalStatus} />
                      </td>

                      {isAdmin(actor) && (
                        <td>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <RowActions
                              label={t.materialName}
                              onEdit={() => setEditing(t)}
                              onDelete={() => confirm.ask(t)}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {adding && Boolean(actor) && <StockTransferDrawer onClose={() => setAdding(false)} />}

      {editing && isAdmin(actor) && (
        <EditStockTransferDrawer
          existing={editing}
          onClose={() => setEditing(null)}
          onDelete={() => {
            confirm.ask(editing);
            setEditing(null);
          }}
        />
      )}

      {confirm.target && (
        <Confirm
          title="Delete this transfer?"
          message="It will disappear from the register straight away. You can undo it from the message that appears."
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}
    </Shell>
  );
}
